import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Car, UtensilsCrossed, Package, MapPin, Shield, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import useEmblaCarousel from "embla-carousel-react";
import { appSounds } from "@/lib/appSounds";

interface OnboardingProps {
  onComplete: () => void;
}

const slides = [
  {
    id: 1,
    icon: Car,
    titleRu: "Такси за минуты",
    titleEn: "Taxi in Minutes",
    descRu: "Закажите такси в любую точку Ингушетии. Быстрая подача, безопасная поездка с проверенными водителями.",
    descEn: "Order a taxi anywhere in Ingushetia. Fast pickup, safe ride with verified drivers.",
    gradient: "from-emerald-500/30 via-teal-500/20 to-cyan-500/30",
    iconBg: "from-emerald-500 to-teal-500",
    iconColor: "text-white",
  },
  {
    id: 2,
    icon: UtensilsCrossed,
    titleRu: "Доставка еды",
    titleEn: "Food Delivery",
    descRu: "Блюда из лучших ресторанов Ингушетии с халяль-меню. Национальная кухня и не только!",
    descEn: "Dishes from the best restaurants in Ingushetia with halal menu. National cuisine and more!",
    gradient: "from-orange-500/30 via-amber-500/20 to-yellow-500/30",
    iconBg: "from-orange-500 to-amber-500",
    iconColor: "text-white",
  },
  {
    id: 3,
    icon: Package,
    titleRu: "Курьерская доставка",
    titleEn: "Courier Delivery",
    descRu: "Отправляйте посылки, документы и подарки. Экспресс-доставка для праздников и важных событий.",
    descEn: "Send parcels, documents and gifts. Express delivery for holidays and special events.",
    gradient: "from-violet-500/30 via-purple-500/20 to-fuchsia-500/30",
    iconBg: "from-violet-500 to-purple-500",
    iconColor: "text-white",
  },
  {
    id: 4,
    icon: MapPin,
    titleRu: "Отслеживание в реальном времени",
    titleEn: "Real-time Tracking",
    descRu: "Следите за заказом на карте. Знайте, где ваш водитель или курьер в любой момент.",
    descEn: "Track your order on the map. Know where your driver or courier is at any moment.",
    gradient: "from-blue-500/30 via-indigo-500/20 to-cyan-500/30",
    iconBg: "from-blue-500 to-indigo-500",
    iconColor: "text-white",
  },
  {
    id: 5,
    icon: Shield,
    titleRu: "Безопасность и комфорт",
    titleEn: "Safety & Comfort",
    descRu: "Возможность выбора водителя-женщины. Все водители проверены и имеют высокий рейтинг.",
    descEn: "Option to choose a female driver. All drivers are verified with high ratings.",
    gradient: "from-green-500/30 via-emerald-500/20 to-teal-500/30",
    iconBg: "from-green-500 to-emerald-500",
    iconColor: "text-white",
  },
];

export const Onboarding = ({ onComplete }: OnboardingProps) => {
  const { language } = useLanguage();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [currentIndex, setCurrentIndex] = useState(0);

  const getTitle = (slide: typeof slides[0]) => {
    if (language === 'en') return slide.titleEn;
    return slide.titleRu;
  };

  const getDesc = (slide: typeof slides[0]) => {
    if (language === 'en') return slide.descEn;
    return slide.descRu;
  };

  const getSkipText = () => {
    if (language === 'en') return 'Skip';
    return 'Пропустить';
  };

  const getNextText = () => {
    if (language === 'en') return 'Next';
    return 'Далее';
  };

  const getStartText = () => {
    if (language === 'en') return 'Get Started';
    return 'Начать';
  };

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    if (newIndex !== currentIndex) {
      appSounds.playSlide();
    }
    setCurrentIndex(newIndex);
  }, [emblaApi, currentIndex]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on("select", onSelect);
      onSelect();
    }
  }, [emblaApi, onSelect]);

  const handleNext = () => {
    if (currentIndex === slides.length - 1) {
      appSounds.playSuccess();
      localStorage.setItem("onboarding_completed", "true");
      onComplete();
    } else {
      emblaApi?.scrollNext();
    }
  };

  const handleSkip = () => {
    localStorage.setItem("onboarding_completed", "true");
    onComplete();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50"
      style={{ 
        background: "linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)"
      }}
    >
      {/* Background gradient that changes with slide */}
      <div className="absolute inset-0 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className={`absolute inset-0 bg-gradient-to-br ${slides[currentIndex].gradient}`}
          />
        </AnimatePresence>
        
        {/* Decorative blobs */}
        <motion.div 
          animate={{ 
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-10 -left-20 w-80 h-80 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            x: [0, -20, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 -right-20 w-96 h-96 bg-teal-500/10 dark:bg-teal-500/15 rounded-full blur-3xl" 
        />
        
        {/* Floating sparkles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${10 + i * 12}%`,
                top: `${15 + (i % 4) * 20}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.4,
              }}
            >
              <Sparkles className="w-4 h-4 text-emerald-400/40" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Logo at top */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="absolute top-6 left-6 flex items-center gap-2 z-20"
      >
        <img 
          src="/favicon.png" 
          alt="Новкъаст GO" 
          className="w-10 h-10 rounded-xl shadow-lg"
        />
        <span className="text-lg font-bold bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
          Новкъаст GO
        </span>
      </motion.div>

      {/* Skip button */}
      <div className="absolute top-6 right-6 z-20">
        <Button
          variant="ghost"
          onClick={handleSkip}
          className="text-muted-foreground hover:text-foreground hover:bg-background/50"
        >
          {getSkipText()}
        </Button>
      </div>

      {/* Carousel */}
      <div className="relative h-full flex flex-col pt-20">
        <div className="flex-1 overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className="flex-shrink-0 w-full h-full flex flex-col items-center justify-center px-8"
              >
                {/* Icon container with animation */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ 
                    scale: currentIndex === index ? 1 : 0.7, 
                    rotate: currentIndex === index ? 0 : -20,
                    opacity: currentIndex === index ? 1 : 0.3 
                  }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 20,
                    delay: 0.1 
                  }}
                  className="relative mb-10"
                >
                  {/* Glow effect */}
                  <motion.div
                    animate={{ 
                      scale: currentIndex === index ? [1, 1.2, 1] : 1,
                      opacity: currentIndex === index ? [0.5, 0.8, 0.5] : 0
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className={`absolute -inset-4 rounded-[40px] bg-gradient-to-br ${slide.iconBg} blur-2xl opacity-30`}
                  />
                  
                  {/* Icon box */}
                  <div className={`relative w-28 h-28 rounded-[32px] bg-gradient-to-br ${slide.iconBg} 
                    flex items-center justify-center shadow-2xl`}
                  >
                    <slide.icon className={`w-14 h-14 ${slide.iconColor}`} />
                  </div>
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ 
                    opacity: currentIndex === index ? 1 : 0, 
                    y: currentIndex === index ? 0 : 30 
                  }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="text-3xl font-bold text-center mb-4 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text"
                >
                  {getTitle(slide)}
                </motion.h2>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ 
                    opacity: currentIndex === index ? 1 : 0, 
                    y: currentIndex === index ? 0 : 30 
                  }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="text-muted-foreground text-center max-w-sm leading-relaxed text-base"
                >
                  {getDesc(slide)}
                </motion.p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section */}
        <div className="px-8 pb-12 space-y-8">
          {/* Progress dots */}
          <div className="flex justify-center gap-3">
            {slides.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => emblaApi?.scrollTo(index)}
                className="relative"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <motion.div
                  animate={{
                    width: currentIndex === index ? 32 : 8,
                    backgroundColor: currentIndex === index 
                      ? "hsl(160, 84%, 45%)" 
                      : "hsl(var(--muted-foreground) / 0.3)"
                  }}
                  transition={{ duration: 0.3 }}
                  className="h-2 rounded-full"
                />
              </motion.button>
            ))}
          </div>

          {/* Next/Start button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              onClick={handleNext}
              className="w-full h-14 text-lg font-semibold rounded-2xl 
                bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600
                text-white shadow-lg shadow-emerald-500/25
                transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/30
                hover:scale-[1.02] active:scale-[0.98]"
            >
              <span>{currentIndex === slides.length - 1 ? getStartText() : getNextText()}</span>
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default Onboarding;
